import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { HomepageLangSwitch } from "./lang-switch";
import { AutonubilInfraAggregation } from "./infra-aggregation";

/*
 * Enterprise security hero.
 *
 * Replaces the former THREE.js "hyperspeed" canvas (heavy on mobile, visually
 * noisy) with a calm, performant navy canvas - radial teal aurora + a faint
 * grid - in the spirit of XM Cyber / Arctic Wolf. All copy is i18n; the visual
 * on the right is the "infrastructure aggregation" atom (see
 * ./infra-aggregation) - many sources flowing into one opsanio core. The real
 * dashboard screenshots live in the product showcase further down the page.
 *
 * Exported as `HeroSectionHyperspeed` for backwards-compatible imports.
 */

const tPrefix = "anketa.autonubil.home.hero";

const ArrowRightIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M10 8.5l5 3.5-5 3.5z" fill="currentColor" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const HeroSectionHyperspeed = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  const chips = [
    translate("anketa.autonubil.home.appliance.feature.cve", { defaultMessage: "CVE intelligence" } as any),
    translate("anketa.autonubil.home.appliance.feature.siem", { defaultMessage: "SIEM" } as any),
    translate("anketa.autonubil.home.appliance.feature.assetManagement", {
      defaultMessage: "Asset management",
    } as any),
    translate("anketa.autonubil.home.appliance.feature.patch.management", {
      defaultMessage: "Patch management",
    } as any),
    translate("anketa.autonubil.home.appliance.audit", { defaultMessage: "Audit" } as any),
  ];

  return (
    <section
      style={{
        position: "relative",
        width: "100%",
        minHeight: "92vh",
        overflow: "hidden",
        background: "var(--ak-hero-bg)",
        color: "var(--ak-on-navy)",
        isolation: "isolate",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* faint grid */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundImage:
            "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(120% 90% at 50% 0%, #000 35%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(120% 90% at 50% 0%, #000 35%, transparent 80%)",
        }}
      />

      {/* language switcher */}
      <div style={{ position: "absolute", top: 22, right: 24, zIndex: 5 }}>
        <HomepageLangSwitch />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1240,
          margin: "0 auto",
          padding: "clamp(96px, 12vh, 140px) clamp(24px, 5vw, 56px)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "clamp(32px, 5vw, 64px)",
        }}
      >
        {/* left: copy */}
        <div style={{ flex: "1 1 440px", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px 6px 10px",
              borderRadius: 999,
              background: "var(--ak-on-navy-surface-2)",
              border: "1px solid var(--ak-accent-border)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.02em",
              color: "var(--ak-accent-2)",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: "var(--ak-accent-2)",
                boxShadow: "0 0 0 3px rgba(120,201,217,0.22)",
              }}
            />
            {translate(`${tPrefix}.badge`, {
              defaultMessage: "Sovereign IT security platform",
            })}
          </div>

          <h1
            style={{
              margin: "22px 0 0",
              fontSize: "clamp(2.3rem, 5vw, 3.9rem)",
              fontWeight: 800,
              lineHeight: 1.08,
              letterSpacing: "-0.025em",
              color: "var(--ak-on-navy)",
            }}
          >
            {translate(`${tPrefix}.title`, {
              defaultMessage: "See every asset. Prove your technical controls.",
            } as any)}{" "}
            <span
              style={{
                background:
                  "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {translate(`${tPrefix}.title.highlight`, {
                defaultMessage: "Keep every byte.",
              } as any)}
            </span>
          </h1>

          <p
            style={{
              margin: "20px 0 0",
              maxWidth: 560,
              fontSize: "clamp(1.02rem, 1.6vw, 1.2rem)",
              lineHeight: 1.65,
              color: "var(--ak-on-navy-secondary)",
            }}
          >
            {translate(`${tPrefix}.subtitle`, {
              defaultMessage:
                "opsanio unifies assets, vulnerabilities and network flows from 40+ systems into one sovereign, on-premises platform - and maps them automatically to NIS2, BSI IT-Grundschutz and ISO 27001.",
            } as any)}
          </p>

          {/* CTAs */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 14,
              marginTop: 32,
            }}
          >
            <a
              href="/home/contact"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                padding: "14px 26px",
                borderRadius: 10,
                fontSize: "1rem",
                fontWeight: 700,
                textDecoration: "none",
                color: "var(--ak-text-on-accent)",
                background:
                  "linear-gradient(135deg, var(--ak-accent) 0%, var(--ak-accent-strong) 100%)",
                boxShadow: "0 8px 22px var(--ak-accent-glow)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 30px var(--ak-accent-glow)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 22px var(--ak-accent-glow)";
              }}
            >
              {translate(`${tPrefix}.cta.primary`, { defaultMessage: "Book a demo" } as any)}
              <ArrowRightIcon />
            </a>

            <a
              href="#product"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                padding: "14px 24px",
                borderRadius: 10,
                fontSize: "1rem",
                fontWeight: 700,
                textDecoration: "none",
                color: "var(--ak-on-navy)",
                background: "var(--ak-on-navy-surface-2)",
                border: "1px solid var(--ak-on-navy-border)",
                transition: "background 0.2s ease, border-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--ak-on-navy-surface)";
                e.currentTarget.style.borderColor = "var(--ak-accent-border)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--ak-on-navy-surface-2)";
                e.currentTarget.style.borderColor = "var(--ak-on-navy-border)";
              }}
            >
              <PlayIcon />
              {translate(`${tPrefix}.cta.secondary`, {
                defaultMessage: "Explore the platform",
              } as any)}
            </a>
          </div>

          {/* capability chips */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 30,
            }}
          >
            {chips.map((chip) => (
              <span
                key={chip}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--ak-on-navy-secondary)",
                  background: "var(--ak-on-navy-surface)",
                  border: "1px solid var(--ak-on-navy-border)",
                }}
              >
                <span style={{ color: "var(--ak-accent-2)", display: "inline-flex" }}>
                  <CheckIcon />
                </span>
                {chip}
              </span>
            ))}
          </div>
        </div>

        {/* right: product mockup */}
        <div
          style={{
            flex: "1 1 380px",
            minWidth: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <AutonubilInfraAggregation />
        </div>
      </div>
    </section>
  );
};

export default HeroSectionHyperspeed;
